import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { BookOpen, Feather, Mail, Award } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';

const Home = () => {
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Person",
        "name": "Laura Novak",
        "jobTitle": "Author",
        "description": "Award-winning journalist and novelist specializing in comedic mystery fiction",
        "url": "https://lauranovakwrites.com",
        "sameAs": [],
        "knowsAbout": ["Fiction Writing", "Mystery Novels", "Journalism", "Comedy Writing"],
        "alumniOf": {
          "@type": "CollegeOrUniversity",
          "name": "Columbia University"
        }
      },
      {
        "@type": "WebSite",
        "name": "Laura Novak - Author",
        "url": "https://lauranovakwrites.com",
        "description": "Official website of Laura Novak, award-winning journalist and author",
        "publisher": {
          "@type": "Person",
          "name": "Laura Novak"
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://lauranovakwrites.com"
          }
        ]
      }
    ]
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Laura Novak - Award-Winning Author & Journalist"
        description="Laura Novak is an award-winning journalist turned novelist. From The New York Times to Berkeley mysteries and Downtown LA comedies, crafting stories that captivate and entertain."
        keywords="Laura Novak, author, novelist, mystery writer, California fiction, NYT journalist, comedic novels, Clari Drake, Berkeley mysteries, Downtown LA"
        url="https://lauranovakwrites.com"
        type="profile"
        schema={schema}
      />
      
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" aria-label="Hero section">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_laura-nova-author/artifacts/4aprzk88_DTLAskyline.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
          role="img"
          aria-label="Downtown Los Angeles skyline at dusk"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/50 via-slate-900/40 to-slate-900/30"></div>
          {/* Photo Credit - Subtle bottom corner */}
          <div className="absolute bottom-3 right-3 z-10">
            <p className="text-sm text-white/90 hover:text-white transition-colors duration-300">
              Photo by Laura Novak
            </p>
          </div>
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <div className="mb-8 animate-fade-in">
            <h1 className="text-6xl md:text-8xl font-serif font-bold text-white mb-6 tracking-tight">
              Laura Novak
            </h1>
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="h-px w-16 bg-amber-400"></div>
              <Feather className="w-6 h-6 text-amber-400" />
              <div className="h-px w-16 bg-amber-400"></div>
            </div>
            <p className="text-2xl md:text-3xl text-slate-300 font-light mb-4">
              Journalist. Storyteller. Novelist.
            </p>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
              From the newsrooms of The New York Times to the quirky streets of Berkeley 
              and the wild terrain of Downtown Los Angeles, crafting stories that captivate and entertain.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
            <Link to="/books">
              <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-lg transition-all duration-300 hover:scale-105">
                <BookOpen className="w-5 h-5 mr-2" />
                Explore My Books
              </Button>
            </Link>
            <Link to="/about">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-6 text-lg transition-all duration-300 hover:scale-105">
                About Me
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Book Trailer Video Section */}
      <section className="py-20 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-block bg-pink-500 text-white px-6 py-2 rounded-full text-sm font-semibold mb-6 animate-pulse">
              A DARKISH COMEDY
            </div>
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4">
              DTLA Confidential
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Book Trailer
            </p>
          </div>
          
          <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-black">
            <video 
              className="w-full h-auto"
              controls
              poster="https://customer-assets-39nsmqrw.emergentagent.net/job_laura-nova-author/artifacts/vlw051lr_Screenshot%202026-07-28%20at%2010.38.49%E2%80%AFAM.png"
              preload="metadata"
            >
              <source 
                src="https://customer-assets-39nsmqrw.emergentagent.net/job_laura-nova-author/artifacts/ntl4n6bg_dtla-confidential.mp4" 
                type="video/mp4" 
              />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Featured Books Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-serif font-bold text-slate-900 mb-4">Featured Works</h2>
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="h-px w-12 bg-amber-600"></div>
              <BookOpen className="w-5 h-5 text-amber-600" />
              <div className="h-px w-12 bg-amber-600"></div>
            </div>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              From Berkeley mysteries to Downtown LA comedy
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Nora's Story */}
            <Card className="group hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 bg-white border-slate-200 flex flex-col">
              <CardContent className="p-8 flex flex-col h-full">
                <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-lg mb-6 flex items-center justify-center shadow-lg py-12 px-6 md:py-16 md:px-10">
                  <div className="text-center w-full max-w-xl mx-auto">
                    <p className="text-white text-sm md:text-base mb-2">
                      Looking to Launch
                    </p>
                    <h3 className="text-2xl md:text-3xl lg:text-4xl font-serif font-bold text-white mb-4 leading-tight">
                      A Los Angeles Comedy
                    </h3>
                    <p className="text-white text-sm md:text-base leading-relaxed">
                      Welcome to Downtown LA, where the rent is high, the stakes are higher, 
                      and the neighbors are the highest of all. A heartwarming ode to chosen family.
                    </p>
                  </div>
                </div>
                <div className="flex-grow">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">
                    DTLA Confidential
                  </h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    A fast, funny romp through Downtown LA where a cast of eccentric Angelenos navigate real estate roulette, relationship chaos, and one outrageous business idea that might just be bad enough to work.
                  </p>
                </div>
                <Link to="/books" className="mt-auto">
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700 transition-colors duration-300">
                    Learn More
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Murder at the Mailbox */}
            <Card className="group hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 bg-white border-slate-200 flex flex-col">
              <CardContent className="p-8 flex flex-col h-full">
                <div className="bg-white h-80 rounded-lg mb-6 flex items-center justify-center shadow-lg overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_laura-nova-author/artifacts/1y1toiuh_Mailbox.png"
                    alt="Murder at the Mailbox book cover"
                    className="h-full w-auto object-contain"
                  />
                </div>
                <div className="flex-grow">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">Murder at the Mailbox</h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    Clari Drake returns, pursuing a spanking good story into the darkest corners of the city everyone loves to laugh at. Join Clari on deadline as she investigates the intersection of lust, betrayal and — podiatry in The People's Republic.
                  </p>
                </div>
                <Link to="/books" className="mt-auto">
                  <Button className="w-full bg-orange-600 hover:bg-orange-700 transition-colors duration-300">
                    Learn More
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Finding Clarity */}
            <Card className="group hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 bg-white border-slate-200 flex flex-col">
              <CardContent className="p-8 flex flex-col h-full">
                <div className="bg-white h-80 rounded-lg mb-6 flex items-center justify-center shadow-lg overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_laura-nova-author/artifacts/ncvfsh5e_FindingClarity.jpg"
                    alt="Finding Clarity book cover"
                    className="h-full w-auto object-contain"
                  />
                </div>
                <div className="flex-grow">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">Finding Clarity</h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    Clari Drake is a reporter turned trouble-making mom who sniffs out a story at her son's elite academy. Laugh along as Clari and her oddball posse investigate the deep pockets and deeper secrets of the People's Republic's wealthiest power brokers.
                  </p>
                </div>
                <Link to="/books" className="mt-auto">
                  <Button className="w-full bg-indigo-600 hover:bg-indigo-700 transition-colors duration-300">
                    Learn More
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-slate-900 mb-6">
                From Journalism to Fiction
              </h2>
              <div className="space-y-4 text-lg text-slate-600 leading-relaxed">
                <p>
                  I have lived my life chasing stories — from New York and London to Los Angeles and San Francisco. I've done live shots on television, penned essays for national newspapers and magazines, and covered Business, Health and the Arts in California for <span className="font-semibold text-slate-800">The New York Times</span>.
                </p>
                <p>
                  Then I created a mystery set in Berkeley, and discovered a new passion for storytelling.
                </p>
              </div>
              <div className="mt-8">
                <Link to="/about">
                  <Button size="lg" className="bg-slate-900 hover:bg-slate-800 text-white px-8 transition-all duration-300 hover:scale-105">
                    Read My Story
                  </Button>
                </Link>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="relative bg-slate-100 rounded-lg overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_laura-nova-author/artifacts/2uaiab95_laura-hat.png" 
                  alt="Laura Novak in Tiburon" 
                  className="rounded-lg shadow-2xl w-full h-[500px] object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Distinguished Career */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <Award className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <h2 className="text-4xl md:text-5xl font-serif font-bold mb-4">Distinguished Career</h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              25+ years in journalism. Three comedic novels.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-slate-800 border-slate-700 hover:border-amber-500 transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="text-5xl font-bold text-amber-400 mb-2">25+</div>
                <p className="text-slate-300">Years in Journalism</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700 hover:border-amber-500 transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="text-5xl font-bold text-amber-400 mb-2">NYT</div>
                <p className="text-slate-300">Contributor</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700 hover:border-amber-500 transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="text-5xl font-bold text-amber-400 mb-2">3</div>
                <p className="text-slate-300">Comedic Novels</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Link to="/news">
              <Button size="lg" variant="outline" className="border-2 border-amber-500 text-amber-400 hover:bg-amber-500 hover:text-slate-900 px-8 transition-all duration-300">
                News
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-amber-50 to-slate-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Mail className="w-16 h-16 text-amber-600 mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-slate-900 mb-6">
            Let's Connect
          </h2>
          <p className="text-xl text-slate-600 mb-8 leading-relaxed">
            Interested in my work? Want to discuss a story or collaboration?
            I'd love to hear from you.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-10 py-6 text-lg transition-all duration-300 hover:scale-105">
              Get In Touch
            </Button>
          </Link>
        </div>
      </section>

      {/* Decorative Separator before Footer */}
      <div className="h-2 bg-gradient-to-r from-transparent via-amber-500 to-transparent opacity-60"></div>
    </div>
  );
};

export default Home;